/**
 * The one error type both transports (axios and the presigned-storage PUT)
 * throw, moved out of the old fetch-based client so `err instanceof ApiError`
 * checks elsewhere keep working regardless of which transport threw.
 *
 * ── ONE ERROR TYPE, WITH THE LAYER THAT FAILED ────────────────────────────
 * The API answers failures as `{ error: { code, message, layer, fields? } }`.
 * `message` is written for the person reading it; `layer` says whether to look
 * at storage, the database or the processing service. A 401 is not an error to
 * shout about — it is the cue to show the sign-in screen — so it has its own
 * check rather than a message.
 */

export interface ApiErrorBody {
	code: string;
	message: string;
	layer?: 'api' | 'database' | 'storage' | 'python' | 'new';
	detail?: string;
	fields?: Record<string, string>;
}

export class ApiError extends Error {
	readonly code: string;
	readonly status: number;
	readonly layer: string;
	readonly detail: string;
	/** Field-level messages, for a form to show beside the input that failed. */
	readonly fields: Record<string, string>;

	constructor(status: number, body: ApiErrorBody) {
		super(body.message);
		this.name = 'ApiError';
		this.status = status;
		this.code = body.code;
		this.layer = body.layer ?? 'api';
		this.detail = body.detail ?? '';
		this.fields = body.fields ?? {};
	}
}

/** The API did not answer at all. A different remedy: start it. */
export class ApiOffline extends Error {
	constructor(readonly cause: string) {
		super('Reconcilio cannot reach the server right now.');
		this.name = 'ApiOffline';
	}
}

export function isUnauthorized(error: unknown): boolean {
	return error instanceof ApiError && error.status === 401;
}

export function isForbidden(error: unknown): boolean {
	return error instanceof ApiError && error.status === 403;
}
